import React, { useEffect } from "react";
import { ActionFunction, redirect } from "react-router-dom";
import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient";
import { Alert, AlertTitle, Box, Container, Typography } from "@mui/material";
import { z } from "zod";
import { Form } from "react-router-dom";
import { useForm } from "react-hook-form";
import { CheckboxElement, SelectElement, TextFieldElement, TextareaAutosizeElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { QueryClient } from "@tanstack/react-query";
import { schemas } from "../../generated/api/client";
import SingleClickWaitingButton from "../../components/SingleClickWaitingButton";
import { useSelectedCustomer } from "../CustomerSelector";
import { TestingInfoBlockFormErrors } from "../../components/TestingInfo";
import { rulesAddJob } from "../../logic/rulesAddJob";
import { normaliseDBSText } from "../../logic/dbsText";
import { applicationTypeMapping, applicationWorkforceMapping, asOptions } from "../../utils/option-data";

const zFormSchema = schemas.NewJob
type FormSchema = z.infer<typeof zFormSchema>

export const Element: React.FC = () => {
  // Build the Form
  const form = useForm<FormSchema>({
    resolver: zodResolver(
      zFormSchema
        .superRefine(rulesAddJob)
    ),
    mode: 'onBlur',
    defaultValues: {
      working_with_adults: false,
      working_with_children: false,
      working_at_home_address: false,
      volunteer: false,
      with_adult_first: true,
    },
  })

  const selectedCustomer = useSelectedCustomer()
  const customerId = selectedCustomer?.customer_id ?? ''
  useEffect(() => {
    form.setValue('customer_id', customerId)
  }, [customerId, form])

  const customer_id = form.watch('customer_id')
  const customer = apiClientHooks.useGetCustomerById({
    params: { id: customer_id },
  }, {
    enabled: !!customer_id,
  }).data
  const isCustomerSectorHealthcare = customer?.sector_healthcare ?? false

  const application_workforce = form.watch('application_workforce')
  const isApplicationWorkforceAdult = application_workforce === 'adult_only'

  const application_type = form.watch('application_type')
  const isApplicationTypeEnhanced = application_type === 'enhanced'

  const working_with_adults = form.watch('working_with_adults')
  const isWorkingWithAdults = working_with_adults === true

  const isAdultFirstEligible = isCustomerSectorHealthcare && isApplicationWorkforceAdult && isApplicationTypeEnhanced && isWorkingWithAdults
  const with_adult_first = form.watch('with_adult_first')
  useEffect(() => {
    // Ensure that the with_adult_first is false if the role is not eligible for Adult First
    if (!isAdultFirstEligible && with_adult_first === true) {
      form.setValue('with_adult_first', false)
    }
  }, [form, isAdultFirstEligible, with_adult_first])

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Helmet>
      <title>New Job Role</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          New Job Role
        </Typography>

        <Form method="post">
          <input type="hidden" name="customer_id" value={customerId} />

          <TextFieldElement
            {...textFieldElementCommonProps}
            label="Job Role"
            name="role"
            inputProps={{ 'data-testid': "role" }}
            // Show the user the typographic characters being corrected rather than silently
            // changing what they typed on save. TextFieldElement runs its own blur handling
            // first, so field-level validation still fires.
            onBlur={event => form.setValue('role', normaliseDBSText(event.target.value), { shouldValidate: true })}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            Choose the application type that the role <strong>requires</strong>.
            You will need to justify this with reference to appropriate legislation.
            <br /><br />
            If you need help, please contact us for guidance.
          </Alert>

          <SelectElement
            {...textFieldElementCommonProps}
            label="Application Type"
            name="application_type"
            data-testid="application_type"
            options={asOptions(applicationTypeMapping)}
          />

          <TextareaAutosizeElement
            {...textFieldElementCommonProps}
            label="Justification for Application Type"
            name="application_type_justification"
            data-testid="application_type_justification"
            rows={5}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            Choose the workforce the role sits in. The DBS requires it on every <strong>standard</strong> and <strong>enhanced</strong> application:
            it is sent with the job role as the Position Applied For. Choose <strong>Other</strong> only for roles that do not relate to
            working with children or adults.
            <br /><br />
            If you need help, please contact us for guidance.
          </Alert>

          <SelectElement
            {...textFieldElementCommonProps}
            label="Application Workforce"
            name="application_workforce"
            options={asOptions(applicationWorkforceMapping)}
            data-testid="application_workforce"
          />

          <TextareaAutosizeElement
            {...textFieldElementCommonProps}
            label="Justification for Application Workforce"
            name="application_workforce_justification"
            data-testid="application_workforce_justification"
            rows={5}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            Please select this only if the role requires this check under the law.
            Justification of what aspect of legislation supports this check is required.
            <br /><br />
            If you need help, please contact us for guidance.
          </Alert>

          <CheckboxElement
            control={form.control}
            label="Working with Adults (barred list)"
            name="working_with_adults"
            data-testid="working_with_adults"
            inputProps={{ name: 'working_with_adults' }}
          />

          <TextareaAutosizeElement
            {...textFieldElementCommonProps}
            label="Justification for Working with Adults (barred list)"
            name="working_with_adults_justification"
            data-testid="working_with_adults_justification"
            rows={5}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            <AlertTitle>Adult First (ISA)</AlertTitle>
            {isAdultFirstEligible
              ? <>
                This role is eligible for the Adult First check.
                Please select this if you wish to apply for the Adult First check.
                <br />
                <CheckboxElement
                  control={form.control}
                  label="With Adult First"
                  name="with_adult_first"
                  data-testid="with_adult_first"
                  inputProps={{ name: 'with_adult_first' }}
                />
              </>
              : <>
                This role is not eligible for the Adult First check. Contact us for guidance if you believe this is incorrect.
                <br />
                <CheckboxElement
                  control={form.control}
                  label="With Adult First"
                  disabled={true}
                  name="with_adult_first"
                  data-testid="with_adult_first"
                  inputProps={{ name: 'with_adult_first' }}
                />

              </>
            }
          </Alert>

          <Alert severity="info" sx={{ marginTop: 5 }}>
            Please select this only if the role requires this check under the law.
            Justification of what aspect of legislation supports this check is required.
            <br /><br />
            If you need help, please contact us for guidance.
          </Alert>

          <CheckboxElement
            control={form.control}
            label="Working with Children (barred list)"
            name="working_with_children"
            data-testid="working_with_children"
            inputProps={{ name: 'working_with_children' }}
          />

          <TextareaAutosizeElement
            {...textFieldElementCommonProps}
            label="Justification for Working with Children (barred list)"
            name="working_with_children_justification"
            data-testid="working_with_children_justification"
            rows={5}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            Does this position involve working with children or adults at the applicant's home address?
            <br /><br />
            If you need help, please contact us for guidance.
          </Alert>

          <CheckboxElement
            control={form.control}
            label="Working at Home Address"
            name="working_at_home_address"
            data-testid="working_at_home_address"
            inputProps={{ name: 'working_at_home_address' }}
          />

          <TextareaAutosizeElement
            {...textFieldElementCommonProps}
            label="Justification for Working from Home"
            name="working_at_home_address_justification"
            data-testid="working_at_home_address_justification"
            rows={5}
          />

          <Alert severity="info" sx={{ marginTop: 5 }}>
            By selecting this, you confirm that this role meets the DBS definition of a free of charge volunteer application.
            Please note that DBS may recover the application fee, if this box is marked in error and this could result in the cancellation of your DBS registration.
          </Alert>

          <CheckboxElement
            control={form.control}
            label="Volunteer"
            name="volunteer"
            data-testid="volunteer"
            inputProps={{ name: 'volunteer' }}
          />

          <TestingInfoBlockFormErrors form={form} />

          <SingleClickWaitingButton
            type="submit"
            fullWidth
            variant="contained"
            color={form.formState.isValid ? 'primary' : 'inherit'}
            onClick={async event => {
              const isValid = await form.trigger()
              if (!isValid) {
                console.warn("Form is not valid", form.formState.errors)
                event.preventDefault()
              }
            }}
            sx={{ mt: 3, mb: 2 }}
          >
            Create
          </SingleClickWaitingButton>
        </Form>
      </Box>
    </Container>
  </>
}

export const action: (queryClient: QueryClient) => ActionFunction = queryClient => async ({ params, request }) => {
  const formData = await request.formData()
  const formObject = Object.fromEntries(formData)
  const FormObjectRetyped = {
    ...formObject,
    // Correct typographic characters here as well as on blur: blur doesn't fire if the form is
    // submitted with the Enter key, and the schema is deliberately strict (DBS-237)
    role: typeof formObject.role === 'string' ? normaliseDBSText(formObject.role).trim() : formObject.role,
    // The above checkboxes are either 'true' if checked, or absent if not
    working_with_adults: formObject.working_with_adults === 'true',
    working_with_children: formObject.working_with_children === 'true',
    working_at_home_address: formObject.working_at_home_address === 'true',
    volunteer: formObject.volunteer === 'true',
    with_adult_first: formObject.with_adult_first === 'true',
  }
  const newJobData = zFormSchema.parse(FormObjectRetyped)

  const createdJob = await apiClient.createJob(newJobData)
  console.log("AddJob.action: ", { newJobData, createdJob })

  // Don't wait for this to complete
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getJobs') })
  return redirect(`/${params.customer_id}/jobs`)
}

export default Element
